import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

/**
 * Service pour interagir avec l'API Stripe
 */
class StripeApiService {
  constructor() {
    this.baseUrl = 'https://api.stripe.com/v1';
    this.config = null;
  }

  /**
   * Charge la configuration Stripe depuis Firestore
   */
  async loadConfig() {
    try {
      const configDoc = await getDoc(doc(db, 'config', 'stripe'));
      if (configDoc.exists()) {
        this.config = configDoc.data();
        return this.config;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors du chargement de la configuration Stripe:', error);
      throw error;
    }
  }

  /**
   * Effectue un appel API vers Stripe
   */
  async makeStripeRequest(endpoint, method = 'GET', body = null) {
    if (!this.config) {
      await this.loadConfig();
    }

    if (!this.config?.secretKey) {
      throw new Error('Clé secrète Stripe non configurée');
    }

    try {
      const options = {
        method,
        headers: {
          'Authorization': `Bearer ${this.config.secretKey}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      };

      if (body) {
        options.body = body;
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, options);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `Erreur Stripe: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de l\'appel API Stripe:', error);
      throw error;
    }
  }

  /**
   * Récupère la liste des produits Stripe
   */
  async getProducts() {
    try {
      const data = await this.makeStripeRequest('/products?limit=100');
      return data.data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des produits Stripe:', error);
      throw error;
    }
  }

  /**
   * Récupère la liste des prix Stripe
   */
  async getPrices() {
    try {
      const data = await this.makeStripeRequest('/prices?limit=100');
      return data.data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des prix Stripe:', error);
      throw error;
    }
  }

  /**
   * Récupère les produits avec leurs prix associés
   */
  async getProductsWithPrices() {
    try {
      const [products, prices] = await Promise.all([
        this.getProducts(),
        this.getPrices()
      ]);

      // Associer les prix aux produits
      const productsWithPrices = products.map(product => {
        const productPrices = prices.filter(price => price.product === product.id);
        return {
          ...product,
          prices: productPrices
        };
      });

      return productsWithPrices;
    } catch (error) {
      console.error('Erreur lors de la récupération des produits avec prix:', error);
      throw error;
    }
  }

  /**
   * Récupère les plans d'abonnement
   */
  async getSubscriptionPlans() {
    try {
      const products = await this.getProductsWithPrices();
      
      // Filtrer les produits qui ont des prix récurrents (abonnements)
      const subscriptionPlans = products.filter(product => 
        product.prices.some(price => price.type === 'recurring')
      );

      return subscriptionPlans;
    } catch (error) {
      console.error('Erreur lors de la récupération des plans d\'abonnement:', error);
      throw error;
    }
  }

  /**
   * Teste la connexion à l'API Stripe
   */
  async testConnection() {
    try {
      const data = await this.makeStripeRequest('/balance');
      return {
        success: true,
        data: data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Crée un produit dans Stripe
   */
  async createProduct(productData) {
    try {
      const formData = new URLSearchParams();
      formData.append('name', productData.name);
      formData.append('description', productData.description || '');
      
      if (productData.metadata) {
        Object.entries(productData.metadata).forEach(([key, value]) => {
          formData.append(`metadata[${key}]`, value);
        });
      }

      const data = await this.makeStripeRequest('/products', 'POST', formData);
      return data;
    } catch (error) {
      console.error('Erreur lors de la création du produit Stripe:', error);
      throw error;
    }
  }

  /**
   * Crée un prix dans Stripe
   */
  async createPrice(priceData) {
    try {
      const formData = new URLSearchParams();
      formData.append('product', priceData.productId);
      formData.append('unit_amount', Math.round(priceData.unitAmount * 100)); // Convertir en centimes
      formData.append('currency', priceData.currency || 'eur');

      // Only add recurring parameters if explicitly specified
      // For one-time invoices, don't include recurring at all
      if (priceData.recurring) {
        formData.append('recurring[interval]', priceData.recurring.interval);

        if (priceData.recurring.interval_count) {
          formData.append('recurring[interval_count]', priceData.recurring.interval_count);
        }
      }

      if (priceData.nickname) {
        formData.append('nickname', priceData.nickname);
      }

      const data = await this.makeStripeRequest('/prices', 'POST', formData);
      return data;
    } catch (error) {
      console.error('Erreur lors de la création du prix Stripe:', error);
      throw error;
    }
  }

  /**
   * Met à jour un produit Stripe
   */
  async updateProduct(productId, productData) {
    try {
      const formData = new URLSearchParams();
      
      if (productData.name) {
        formData.append('name', productData.name);
      }
      
      if (productData.description !== undefined) {
        formData.append('description', productData.description);
      }
      
      if (productData.active !== undefined) {
        formData.append('active', productData.active.toString());
      }
      
      if (productData.metadata) {
        Object.entries(productData.metadata).forEach(([key, value]) => {
          formData.append(`metadata[${key}]`, value);
        });
      }

      const data = await this.makeStripeRequest(`/products/${productId}`, 'POST', formData);
      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du produit Stripe:', error);
      throw error;
    }
  }

  /**
   * Met à jour un prix Stripe
   */
  async updatePrice(priceId, priceData) {
    try {
      const formData = new URLSearchParams();
      
      if (priceData.active !== undefined) {
        formData.append('active', priceData.active.toString());
      }
      
      if (priceData.nickname !== undefined) {
        formData.append('nickname', priceData.nickname);
      }

      const data = await this.makeStripeRequest(`/prices/${priceId}`, 'POST', formData);
      return data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du prix Stripe:', error);
      throw error;
    }
  }

  /**
   * Supprime un produit Stripe (le rend inactif)
   */
  async deleteProduct(productId) {
    try {
      const data = await this.makeStripeRequest(`/products/${productId}`, 'DELETE');
      return data;
    } catch (error) {
      console.error('Erreur lors de la suppression du produit Stripe:', error);
      throw error;
    }
  }

  /**
   * Archive un prix Stripe (le rend inactif)
   * Note: Les prix ne peuvent pas être supprimés dans Stripe, seulement archivés
   */
  async deletePrice(priceId) {
    try {
      // Les prix Stripe ne peuvent pas être supprimés, seulement archivés
      const data = await this.makeStripeRequest(`/prices/${priceId}`, 'POST', {
        active: false
      });
      console.log(`✅ Prix ${priceId} archivé (désactivé)`);
      return data;
    } catch (error) {
      console.error('Erreur lors de l\'archivage du prix Stripe:', error);
      throw error;
    }
  }
}

// Export d'une instance unique
export default new StripeApiService();
